"use client";

import React, { useEffect, useRef } from "react";
import "./vendor-spend.css";

const vendorSpendImages = {
  banner: "/assets/images/vendor-spend-banner.jpg",
  insightToAction: "/assets/images/INSIGHT TO ACTION.png",
  illustrativeUseCase: "/assets/images/ILLUSTRATIVE-USE-CASE.jpg",
  relatedBusinessQuestions: "/assets/images/RELATED-BUSINESS-QUESTIONS.jpg",
  cta: "/assets/images/vendor-spend-cta.jpg",
  fromSpendVisibility: "/assets/images/from-spend-visibility.jpg",
};

const questions = [
  "Why are purchase prices increasing?",
  "Are we paying different prices for the same thing?",
  "Are duplicate vendors hiding in our master data?",
  "Which vendors drive avoidable spend?",
  "Which suppliers affect service or cost?",
  "Where are purchases happening outside contracts?",
  "Are PO–GR–invoice exceptions indicating leakage?",
  "Which vendors represent concentration risk?",
  "Are we getting the right quality at the right price?",
  "How can we improve supplier performance?",
  "Where are maverick or non-compliant purchases?",
];

const areas = [
  ["Spend Intelligence", "Total spend · Savings · Avoidable spend · Category analysis"],
  ["Vendor Intelligence", "Vendor master · Spend concentration · Diversity · Duplicate vendors"],
  ["Price Intelligence", "List vs. actual price · Variance · Benchmarking · Opportunities"],
  ["Contract Intelligence", "Coverage · Compliance · Expiry · Terms and conditions"],
  ["Procurement Exceptions", "PO–GR–invoice mismatch · Blocked invoices · Non-contract spend"],
  ["Supplier Risk & Performance", "Delivery · Quality · Service · Risk · ESG · Continuity"],
];

const outcomes = [
  ["01", "Lower procurement cost", "Find savings and reduce avoidable spend."],
  ["02", "Better supplier performance", "Track delivery, quality, service and risk."],
  ["03", "Higher compliance", "See contract coverage and policy exceptions."],
  ["04", "Reduced maverick spend", "Reveal off-contract buying and process gaps."],
  ["05", "Improved working capital", "See payment delays and working-capital impact."],
  ["06", "Lower supply risk", "Identify supplier dependencies and exposure."],
  ["07", "Transparent decisions", "Create a shared, evidence-led spend view."],
];

const actions = [
  ["What happened?", "See current spend patterns, movements and exceptions."],
  ["Where?", "Locate the category, supplier, entity or transaction involved."],
  ["Why?", "Understand the drivers behind the variance or exception."],
  ["What can change?", "Review supplier, price, contract, specification or process options."],
  ["What is the impact?", "Estimate savings, risk reduction and performance improvement."],
  ["Take action", "Prioritise, negotiate, consolidate, re-source or resolve."],
  ["Measure value", "Track realised savings and sustained performance over time."],
];

const systems = ["SAP S/4HANA", "Ariba", "ERP", "AP / Finance", "Contract Management", "WMS", "EAM / PM", "External Data"];
const flow = ["Business need", "Source", "Vendor", "Contract", "Purchase order", "Goods receipt", "Invoice", "Payment"];
const layers = [
  ["MASTER DATA", "Materials & services", "Vendor & supplier", "Contracts", "Categories", "Organisation & approvals"],
  ["TRANSACTIONS", "Requisitions", "Purchase orders", "Goods receipts", "Invoices", "Payments"],
  ["CONNECTED DATA", "ERP", "Ariba", "AP / Finance", "Other systems", "External data"],
];

/* ---------- Scroll reveal (bottom to top) ---------- */

function useScrollReveal() {
  const ref = useRef(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const items = root.querySelectorAll("[data-reveal]");

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || !("IntersectionObserver" in window)) {
      items.forEach((el) => el.classList.add("is-visible"));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -60px 0px" }
    );

    items.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return ref;
}

const delay = (i, step = 80, max = 400) => ({
  "--vs-delay": `${Math.min(i * step, max)}ms`,
});

/* ---------- Components ---------- */

function Heading({ kicker, title, text }) {
  return (
    <header className="vs-heading" data-reveal>
      <span className="table">{kicker}</span>
      <h2>{title}</h2>
      <p>{text}</p>
    </header>
  );
}

export default function VendorSpendPage() {
  const ref = useScrollReveal();

  return (
    <main className="vendor-spend" ref={ref}>
      <section className="vs-hero">
        <img className="vs-hero-banner" src={vendorSpendImages.banner} alt="" aria-hidden="true" />
        <div className="vs-wrap vs-hero-grid">
          <div className="vs-hero-copy">
            {/* <div className="vs-crumb">What We Do <b>›</b> Vendor &amp; Spend</div> */}
            {/* <span className="vs-kicker">VENDOR &amp; SPEND INTELLIGENCE</span> */}
            <h1 data-reveal style={delay(0)}>Know your suppliers.<br />Track what you buy.<br />Identify where value leaks.</h1>
            <p data-reveal style={delay(1, 150)}>From sourcing to payment, connect people, process and data to strengthen suppliers, improve visibility and bring greater control to spend.</p>
            <div className="vs-actions" data-reveal style={delay(2, 150)}>
              <a className="vs-btn vs-btn-blue fp-buttons fp-btn-primary " href="/tellusyourproblem#tell-details">Tell Us Your Vendor &amp; Spend Problem </a>
              <a className="fp-buttons fp-hero-outline-btn" href="#process">See How It Works</a>
            </div>
            {/* <div className="vs-proof"><span>Supplier visibility</span><i /> <span>Spend control</span><i /> <span>Actionable insights</span></div> */}
          </div>
          {/* <div className="vs-hero-art" aria-label="Connected spend intelligence visual"><img className="vs-hero-photo" src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1200&q=85" alt="Professional reviewing business reports at a desk" />
            <div className="vs-orbit vs-orbit-a" /><div className="vs-orbit vs-orbit-b" />
            <div className="vs-art-core"><span>◎</span><b>Spend<br />Intelligence</b><small>Connected view</small></div>
            <div className="vs-art-card vs-card-top"><b>↗</b><span><small>Spend visibility</small><strong>One connected view</strong></span></div>
            <div className="vs-art-card vs-card-left"><b>▤</b><span><small>Supplier data</small><strong>Trusted master</strong></span></div>
            <div className="vs-art-card vs-card-right"><b>✓</b><span><small>Value outcomes</small><strong>Measured impact</strong></span></div>
            <div className="vs-art-caption">Source <i>→</i> Contract <i>→</i> Purchase <i>→</i> Payment</div>
          </div> */}
        </div>
      </section>

      <section className="vs-section vs-questions">
        <div className="vs-wrap">
          <Heading kicker="START WITH THE BUSINESS QUESTION" title="The questions procurement leaders ask" text="Spot the signals that matter across cost, suppliers and compliance." />
          <div className="vs-question-list">
            {questions.map((q, i) => (
              <div className="vs-question" key={q} data-reveal style={delay(Math.floor(i / 2), 70)}>
                <b>{String(i + 1).padStart(2, "0")}</b>
                <span>{q}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="vs-section" id="process">
        <div className="vs-wrap">
          <Heading kicker="A CONNECTED PROCESS" title="End-to-end source to pay" text="Connect each step from business need through payment." />
          <div className="vs-flow">
            {flow.map((step, i) => (
              <React.Fragment key={step}>
                <div
                  className={i === flow.length - 1 ? "vs-flow-step vs-flow-final" : "vs-flow-step"}
                  data-reveal
                  style={delay(i, 70, 490)}
                >
                  <b>{["⌕", "⇄", "♙", "▤", "▣", "▱", "▧", "▰"][i]}</b>
                  <span>{step}</span>
                </div>
                {i < flow.length - 1 && <i className="vs-flow-arrow">→</i>}
              </React.Fragment>
            ))}
          </div>
          <div className="vs-layers">
            {layers.map(([label, ...items], i) => (
              <div className="vs-layer" key={label} data-reveal style={delay(i, 120)}>
                <div className="vs-layer-title"><small>0{i + 1}</small><b>{label}</b></div>
                <div className="vs-layer-items">{items.map((item) => <span key={item}>{item}</span>)}</div>
              </div>
            ))}
          </div>
          <div className="vs-intel-band" data-reveal>
            <b>✳</b>
            <span><strong>SPEND INTELLIGENCE</strong><small>Spend · Suppliers · Contracts · Risk · Performance · Value</small></span>
            <em>From fragmented records to connected insight</em>
          </div>
        </div>
      </section>

      <section className="vs-section vs-soft vs-outcomes-section">
        <div className="vs-wrap">
          <div className="vs-outcomes-layout">
            <div className="vs-outcomes-intro" data-reveal>
              <img
                className="vs-outcomes-bg"
                src={vendorSpendImages.fromSpendVisibility}
                alt=""
                aria-hidden="true"
                loading="lazy"
              />
              <span className="vs-kicker">VALUE YOU CAN SEE</span>
              <h2>From spend visibility to business impact.</h2>
              <p>Turn procurement data into measurable outcomes across cost, suppliers and compliance.</p>
              <div className="vs-outcomes-summary">
                <strong>7 outcomes</strong>
                <span>One connected view. Clearer decisions.</span>
              </div>
            </div>
            <div className="vs-outcomes-list">
              {outcomes.map(([n, title, text], i) => (
                <article className="vs-outcome-row" key={n} data-reveal style={delay(i, 80, 480)}>
                  <b className="vs-outcome-number">{n}</b>
                  <div className="vs-outcome-copy"><h3>{title}</h3><p>{text}</p></div>
                  <i aria-hidden="true">↗</i>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="vs-section">
        <div className="vs-wrap">
          <Heading kicker="CAPABILITY AREAS" title="Vendor &amp; spend intelligence areas" text="Practical intelligence across the procurement lifecycle." />
          <div className="vs-areas">
            {areas.map(([title, text], i) => (
              <article className="vs-area" key={title} data-reveal style={delay(i % 3, 100)}>
                <small>0{i + 1}</small>
                <b>{["▥", "♙", "◇", "▤", "⚑", "⚙"][i]}</b>
                <h3>{title}</h3>
                <p>{text}</p>
                <a href="/tellusyourproblem#tell-details">Explore capability <i>→</i></a>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="vs-section vs-soft">
        <div className="vs-wrap">
          <Heading kicker="ILLUSTRATIVE USE CASE" title="From a pricing question to a measurable action" text="A simple example of turning a price variance into an informed action." />
          <div className="vs-example">
            <div className="vs-example-visual" data-reveal>
              <img src={vendorSpendImages.illustrativeUseCase} alt="Business analytics and financial charts on a screen" />
            </div>
            <div className="vs-example-copy" data-reveal style={delay(1, 120)}>
              <small>WORKED QUESTION</small>
              <h3>“Are we paying different prices for the same thing?”</h3>
              <p>Compare like-for-like material purchases, then investigate the context behind each variance.</p>
              <div><label>Material</label><b>Packaging film · same / similar specification</b></div>
            </div>
            <div className="vs-table" data-reveal style={delay(2, 120)}>
              <div className="vs-table-head"><span>Plant</span><span>Vendor</span><span>Price / unit</span><span>Volume</span></div>
              {[["Plant A", "Vendor 1", "100", "450"], ["Plant B", "Vendor 2", "108", "380"], ["Plant C", "Vendor 3", "117", "600"], ["Plant D", "Vendor 1", "103", "520"]].map((row) => (
                <div className="vs-table-row" key={row[0]}>
                  {row.map((cell, i) => <span className={i === 2 && cell !== "100" ? "vs-variance" : ""} key={i}>{cell}</span>)}
                </div>
              ))}
              <small className="vs-note">Illustrative figures for demonstration only.</small>
            </div>
          </div>
          <div className="vs-resolution">
            {[["Investigate", "Vendor · Volume · Location · Contract · Freight · Specification"], ["Compare", "Normalise units, specifications and purchasing context."], ["Act", "Negotiate, consolidate or source based on verified comparisons."], ["Measure", "Track realised savings and sustain performance."]].map(([title, text], i) => (
              <React.Fragment key={title}>
                <article
                  className={i > 1 ? "vs-resolution-card vs-resolution-emphasis" : "vs-resolution-card"}
                  data-reveal
                  style={delay(i, 120)}
                >
                  <small>0{i + 1} / {["DISCOVER", "ANALYSE", "RESPOND", "TRACK"][i]}</small>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </article>
                {i < 3 && <span>→</span>}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      <section className="vs-section">
        <div className="vs-wrap">
          <Heading kicker="INSIGHT TO ACTION" title="A structured path from data to decisions" text="A repeatable path from signal to measurable improvement." />
          <div className="vs-action-layout">
            <div className="vs-action-list">
              {actions.map(([title, text], i) => (
                <article className="vs-action" key={title} data-reveal style={delay(i, 80, 480)}>
                  <b>{String(i + 1).padStart(2, "0")}</b>
                  <div><h3>{title}</h3><p>{text}</p></div>
                  <i>↗</i>
                </article>
              ))}
            </div>
            <div className="vs-action-image" data-reveal style={delay(1, 150)}>
              <img src={vendorSpendImages.insightToAction} alt="Spend analytics dashboard with business charts" />
            </div>
          </div>
        </div>
      </section>

      <section className="vs-section vs-soft">
        <div className="vs-wrap">
          <Heading kicker="RELATED BUSINESS QUESTIONS" title="Explore related business problems" text="Related questions across procurement and operations." />
          <div className="vs-problems-layout">
            <div className="vs-problems-image" data-reveal>
              <img src={vendorSpendImages.relatedBusinessQuestions} alt="Modern business workspace with a team collaborating" />
              <div className="vs-problems-image-caption"><small>CONNECTED BUSINESS INSIGHT</small><strong>Questions that lead to action.</strong></div>
            </div>
            <div className="vs-problems-content">
              <div className="vs-problems">
                {["Why are purchase prices increasing?", "Where is supplier performance costing us money?", "Which purchase orders are likely to be delayed?", "Why does the production plan keep changing?", "Which assets are likely to fail?", "Why is maintenance cost rising?", "Where is supply-chain working capital trapped?", "Are we getting the right value from our vendors?"].map((q, i) => (
                  <a href="#contact" key={q} data-reveal style={delay(i, 70, 490)}>
                    <b>{String(i + 7).padStart(2, "0")}</b>
                    <span>{q}</span>
                    <i>→</i>
                  </a>
                ))}
              </div>
              <a className="vs-all-link" href="/52problem#all-problems">Explore all 52 business problems <b>→</b></a>
            </div>
          </div>
        </div>
      </section>

      <section className="vs-section">
        <div className="vs-wrap">
          <Heading kicker="CONNECTED ENTERPRISE DATA" title="Enterprise systems &amp; data" text="Unify core platforms and supporting data sources." />
          <div className="vs-systems">
            {systems.map((s, i) => (
              <div key={s} data-reveal style={delay(i % 8, 60)}>
                <b>{["SAP", "Ar", "ERP", "AP", "CM", "WM", "EM", "↗"][i]}</b>
                <span>{s}</span>
              </div>
            ))}
          </div>
          <div className="vs-data-stack">
            <div data-reveal><b>TRUSTED SPEND DATA</b><small>Consistent, governed and connected information</small></div>
            <i>↓</i>
            <div data-reveal style={delay(1, 120)}><b>BUSINESS CONTEXT</b><small>Material · Vendor · Contract · Category · Organisation · Market data</small></div>
            <i>↓</i>
            <div className="vs-stack-final" data-reveal style={delay(2, 120)}><b>VENDOR &amp; SPEND INTELLIGENCE</b><small>Insight to action, with measurable value</small></div>
          </div>
        </div>
      </section>

      <section className="vs-cta" id="contact">
        <div className="vs-cta-visual" aria-hidden="true">
          <img src={vendorSpendImages.cta} alt="" loading="lazy" />
        </div>
        <div className="vs-wrap vs-cta-inner">
          <div className="vs-cta-copy">
            <span className="vs-kicker vs-kicker2" data-reveal>LET'S SOLVE THE RIGHT PROBLEM</span>
            <h2 data-reveal style={delay(1, 120)}>Smarter spend. Stronger suppliers.</h2>
            <p data-reveal style={delay(2, 120)}>Let’s discuss your procurement, supplier or spend priorities.</p>
           <a className="vs-btn vs-btn-blue" href="/tellusyourproblem#tell-details" data-reveal style={delay(3, 120)}>
  Talk to Our Team <b>→</b>
</a>
          </div>
        </div>
      </section>
    </main>
  );
}